import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, ReportView, Select } from '../../../components/ui';
import { Hint } from '../../../components/ui/Hint';
import { fetcher } from '../../../services/fetcher';
import { RestRecordReport, type RestRecordSheet } from './RestRecordReport';
import { exportRestRecordToExcel } from './exportRestRecordExcel';
import { useManagementCompanyLogoBlobUrl } from './useManagementCompanyLogo';

function monthShortLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const RestRecordReportPage: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vesselId = params.get('vesselId')?.trim() || '';
  const month = params.get('month')?.trim() || '';
  const crewIdParam = params.get('crewId')?.trim() || '';
  const crewId = crewIdParam ? Number(crewIdParam) : null;
  const crewIdFilter = crewId != null && Number.isFinite(crewId) && crewId > 0 ? crewId : null;
  const [annotateCompliance, setAnnotateCompliance] = useState(false);

  const key =
    vesselId && month
      ? `/wrh/sheet?vesselId=${encodeURIComponent(vesselId)}&month=${encodeURIComponent(month)}`
      : null;
  const { data, error, isLoading } = useSWR<RestRecordSheet>(key, fetcher, {
    revalidateOnFocus: false,
  });
  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    data?.managementCompany?.id,
    data?.managementCompany?.logoStorageKey,
  );

  const backHref = useMemo(() => {
    const q = new URLSearchParams();
    if (vesselId) q.set('vesselId', vesselId);
    if (month) q.set('month', month);
    const s = q.toString();
    return s ? `/wrh/sheet?${s}` : '/wrh/sheet';
  }, [vesselId, month]);

  const crewOptions = useMemo(() => {
    if (!data?.crew.length) return [{ value: '', label: 'All crew' }];
    return [
      { value: '', label: `All crew (${data.crew.length})` },
      ...data.crew.map((c) => ({
        value: String(c.crewId),
        label: c.rankName ? `${c.crewName} · ${c.rankName}` : c.crewName,
      })),
    ];
  }, [data?.crew]);

  const crewList = useMemo(() => {
    if (!data) return [];
    if (crewIdFilter == null) return data.crew;
    return data.crew.filter((c) => c.crewId === crewIdFilter);
  }, [data, crewIdFilter]);

  const setCrewFilter = (next: string) => {
    const q = new URLSearchParams();
    q.set('vesselId', vesselId);
    q.set('month', month);
    if (next) q.set('crewId', next);
    navigate(`/wrh/sheet/rest-record?${q.toString()}`, { replace: true });
  };

  if (!vesselId || !month) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>
          Open the month sheet and choose vessel / month first.
        </p>
        <Link to="/wrh/sheet" className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to month sheet
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>Could not load WRH month sheet.</p>
        <Link to={backHref} className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to month sheet
        </Link>
      </div>
    );
  }

  const empty = !crewList.length;
  const vesselLabel = data.vesselAbrv
    ? `${data.vesselName} (${data.vesselAbrv})`
    : data.vesselName;

  return (
    <ReportView
      backTo={backHref}
      backLabel="Back to month sheet"
      title="Rest record"
      subtitle={`${vesselLabel} · ${monthShortLabel(data.month)}`}
      printPageSize="A4 landscape"
      empty={empty}
      emptyMessage={
        crewIdFilter != null
          ? 'That crew member has no WRH days in this month.'
          : 'No crew found for this vessel / month. Open the month sheet and ensure days are prepared.'
      }
      onExportExcel={() =>
        exportRestRecordToExcel({
          data,
          crewId: crewIdFilter,
          annotateCompliance,
        })
      }
      actions={
        <>
          <div className="w-[13.5rem]">
            <Select
              value={crewIdFilter != null ? String(crewIdFilter) : ''}
              onChange={(e) => setCrewFilter(String(e.target.value))}
              options={crewOptions}
              placeholder="Seafarer"
              size="sm"
              showClearButton={false}
            />
          </div>
          <div
            className={cn('inline-flex rounded-lg border p-0.5', colors.border)}
            role="group"
            aria-label="Form mode"
          >
            <Hint
              content="Clean official form for signing"
              side="bottom"
              delay={180}
              triggerClassName="inline-flex"
            >
              <button
                type="button"
                onClick={() => setAnnotateCompliance(false)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  !annotateCompliance
                    ? cn(colors.primary, colors.primaryText)
                    : cn(colors.textSecondary, colors.interactiveHover),
                )}
              >
                Clean
              </button>
            </Hint>
            <Hint
              content="Highlight STCW non-compliant days"
              side="bottom"
              delay={180}
              triggerClassName="inline-flex"
            >
              <button
                type="button"
                onClick={() => setAnnotateCompliance(true)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  annotateCompliance
                    ? cn(colors.primary, colors.primaryText)
                    : cn(colors.textSecondary, colors.interactiveHover),
                )}
              >
                Annotated
              </button>
            </Hint>
          </div>
        </>
      }
    >
      <RestRecordReport
        data={data}
        crewId={crewIdFilter}
        annotateCompliance={annotateCompliance}
        companyLogoSrc={companyLogoSrc}
      />
    </ReportView>
  );
};

export default RestRecordReportPage;
